
import { useState } from 'react';
import { Product, ImportRecord } from '../page';

interface ImportFormProps {
  products: Product[];
  onSubmit: (importData: Omit<ImportRecord, 'id' | 'importDate'>) => void;
  onCancel: () => void;
}

export default function ImportForm({ products, onSubmit, onCancel }: ImportFormProps) {
  const [formData, setFormData] = useState({
    productId: '',
    productName: '',
    size: '',
    color: '',
    quantity: 0,
    note: ''
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.productId) {
      newErrors.productId = 'Vui lòng chọn sản phẩm';
    }

    if (!formData.size) {
      newErrors.size = 'Vui lòng chọn size';
    }

    if (!formData.color) {
      newErrors.color = 'Vui lòng chọn màu sắc';
    }

    if (formData.quantity <= 0) {
      newErrors.quantity = 'Số lượng phải lớn hơn 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleProductChange = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setFormData({
        ...formData,
        productId: product.id,
        productName: product.name,
        size: '',
        color: ''
      });
    } else {
      setSelectedProduct(null);
      setFormData({
        ...formData,
        productId: '',
        productName: '',
        size: '',
        color: ''
      });
    }
  };

  const getCurrentQuantity = () => {
    if (!selectedProduct || !formData.size || !formData.color) return 0;
    const variant = selectedProduct.variants.find(v => v.size === formData.size && v.color === formData.color);
    return variant ? variant.quantity : 0;
  };

  const getNewQuantity = () => {
    return getCurrentQuantity() + formData.quantity;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nhập hàng</h1>
          <p className="text-gray-600">Nhập thêm số lượng cho sản phẩm hiện có</p>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-600 hover:text-gray-800"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
          {/* Product Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chọn sản phẩm *
            </label>
            <select
              value={formData.productId}
              onChange={(e) => handleProductChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8 ${
                errors.productId ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Chọn sản phẩm cần nhập hàng</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>
                  {product.id} - {product.name} ({product.category})
                </option>
              ))}
            </select>
            {errors.productId && <p className="text-red-500 text-sm mt-1">{errors.productId}</p>}
          </div>

          {/* Product Details */}
          {selectedProduct && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">Thông tin sản phẩm</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Tên sản phẩm:</span>
                  <span className="ml-2 font-medium">{selectedProduct.name}</span>
                </div>
                <div>
                  <span className="text-gray-600">Danh mục:</span>
                  <span className="ml-2 font-medium">{selectedProduct.category}</span>
                </div>
                <div>
                  <span className="text-gray-600">Giá sản xuất:</span>
                  <span className="ml-2 font-medium">
                    {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' })
                      .format(selectedProduct.productionPrice)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Giá bán:</span>
                  <span className="ml-2 font-medium">
                    {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' })
                      .format(selectedProduct.sellingPrice)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Size Selection */}
          {selectedProduct && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chọn size *
              </label>
              <select
                value={formData.size}
                onChange={(e) => setFormData({ ...formData, size: e.target.value, color: '' })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8 ${
                  errors.size ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Chọn size</option>
                {selectedProduct.sizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              {errors.size && <p className="text-red-500 text-sm mt-1">{errors.size}</p>}
            </div>
          )}

          {/* Color Selection */}
          {selectedProduct && formData.size && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chọn màu sắc *
              </label>
              <select
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8 ${
                  errors.color ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Chọn màu sắc</option>
                {selectedProduct.colors.map(color => (
                  <option key={color} value={color}>{color}</option>
                ))}
              </select>
              {errors.color && <p className="text-red-500 text-sm mt-1">{errors.color}</p>}
            </div>
          )}

          {/* Current Stock Info */}
          {selectedProduct && formData.size && formData.color && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Thông tin tồn kho hiện tại</h4>
              <div className="text-sm text-blue-800">
                <p>Size: <span className="font-medium">{formData.size}</span></p>
                <p>Màu sắc: <span className="font-medium">{formData.color}</span></p>
                <p>Số lượng hiện tại: <span className="font-bold">{getCurrentQuantity()} cái</span></p>
              </div>
            </div>
          )}

          {/* Quantity Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Số lượng nhập *
            </label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.quantity ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Nhập số lượng cần nhập"
              min="1"
            />
            {errors.quantity && <p className="text-red-500 text-sm mt-1">{errors.quantity}</p>}
            
            {formData.quantity > 0 && selectedProduct && formData.size && formData.color && (
              <p className="text-sm text-green-600 mt-1">
                Số lượng sau khi nhập: <span className="font-bold">{getNewQuantity()} cái</span>
              </p>
            )}
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ghi chú
            </label>
            <textarea
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ghi chú về lần nhập hàng này (tùy chọn)"
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-1">{formData.note.length}/500 ký tự</p>
          </div>

          {/* Summary */}
          {formData.quantity > 0 && selectedProduct && formData.size && formData.color && (
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-2">Tóm tắt nhập hàng</h4>
              <div className="text-sm text-green-800 space-y-1">
                <p>Sản phẩm: <span className="font-medium">{selectedProduct.name}</span></p>
                <p>Biến thể: <span className="font-medium">{formData.size} - {formData.color}</span></p>
                <p>Số lượng nhập: <span className="font-bold">+{formData.quantity} cái</span></p>
                <p>Tổng sau nhập: <span className="font-bold">{getNewQuantity()} cái</span></p>
                <p>Giá trị nhập: <span className="font-bold">
                  {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' })
                    .format(formData.quantity * selectedProduct.productionPrice)}
                </span></p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg whitespace-nowrap"
            >
              Hủy
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg whitespace-nowrap"
              disabled={!selectedProduct || !formData.size || !formData.color || formData.quantity <= 0}
            >
              Xác nhận nhập hàng
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
